/**
 * Drawdown Survival Mock
 *
 * Table showing account balance after 10 consecutive losses
 * at different risk-per-trade levels. Makes the survival
 * mathematics of risk management visceral and concrete.
 */

interface RiskLevel {
  risk: string;
  color: string;
  balances: number[];
  verdict: string;
  verdictColor: string;
}

function pctBalance(start: number, riskPct: number, losses: number): number {
  return start * Math.pow(1 - riskPct / 100, losses);
}

const riskLevels: RiskLevel[] = [
  {
    risk: '1%',
    color: '#22c55e',
    balances: [1, 2, 5, 10].map((n) => Math.round(pctBalance(10000, 1, n))),
    verdict: 'Survivable',
    verdictColor: '#22c55e',
  },
  {
    risk: '2%',
    color: '#22c55e',
    balances: [1, 2, 5, 10].map((n) => Math.round(pctBalance(10000, 2, n))),
    verdict: 'Manageable',
    verdictColor: '#22c55e',
  },
  {
    risk: '5%',
    color: '#f59e0b',
    balances: [1, 2, 5, 10].map((n) => Math.round(pctBalance(10000, 5, n))),
    verdict: 'Painful',
    verdictColor: '#f59e0b',
  },
  {
    risk: '10%',
    color: '#ef4444',
    balances: [1, 2, 5, 10].map((n) => Math.round(pctBalance(10000, 10, n))),
    verdict: 'Dangerous',
    verdictColor: '#ef4444',
  },
  {
    risk: '25%',
    color: '#ef4444',
    balances: [1, 2, 5, 10].map((n) => Math.round(pctBalance(10000, 25, n))),
    verdict: 'Terminal',
    verdictColor: '#ef4444',
  },
];

const lossCols = ['1 Loss', '2 Losses', '5 Losses', '10 Losses'];

function fmt(v: number): string {
  return '$' + v.toLocaleString('en-US');
}

export default function DrawdownSurvivalMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Drawdown Survival Table
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem 1rem' }}>
        <div style={{
          fontSize: '0.625rem', color: '#6b7280', marginBottom: '0.5rem',
          textAlign: 'center', fontStyle: 'italic',
        }}>
          Starting balance: $10,000 &mdash; consecutive losses at each risk level
        </div>

        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '80px repeat(4, 1fr) 90px',
          gap: '0.25rem', marginBottom: '0.25rem',
        }}>
          <div style={{
            fontSize: '0.5625rem', fontWeight: 700, color: '#6b7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            padding: '0.25rem 0.375rem',
          }}>Risk / Trade</div>
          {lossCols.map((col) => (
            <div key={col} style={{
              fontSize: '0.5625rem', fontWeight: 700, color: '#6b7280',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              textAlign: 'center', padding: '0.25rem 0',
            }}>{col}</div>
          ))}
          <div style={{
            fontSize: '0.5625rem', fontWeight: 700, color: '#6b7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            textAlign: 'center', padding: '0.25rem 0',
          }}>Verdict</div>
        </div>

        {/* Table rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {riskLevels.map((r) => (
            <div key={r.risk} style={{
              display: 'grid',
              gridTemplateColumns: '80px repeat(4, 1fr) 90px',
              gap: '0.25rem', alignItems: 'center',
              padding: '0.4rem 0.375rem',
              background: '#181825',
              border: `1px solid ${r.color}20`,
              borderRadius: 5,
            }}>
              <div style={{
                fontSize: '0.75rem', fontWeight: 700, color: r.color,
                fontFamily: 'ui-monospace, monospace',
              }}>
                {r.risk}
              </div>
              {r.balances.map((b, i) => (
                <div key={i} style={{
                  fontSize: '0.6875rem', fontWeight: 600, color: '#d1d5db',
                  fontFamily: 'ui-monospace, monospace', textAlign: 'center',
                }}>
                  {fmt(b)}
                </div>
              ))}
              <div style={{
                fontSize: '0.625rem', fontWeight: 600, color: r.verdictColor,
                textAlign: 'center',
              }}>
                {r.verdict}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#ef4444', fontWeight: 600 }}>The math: </span>
          At 1% risk, 10 losses in a row costs 9.6% of your account.
          At 25% risk, the same streak destroys 94.4%. Losing streaks are inevitable
          — risk sizing determines whether you survive them.
        </div>
      </div>
      <div className="platform-mock-caption">
        The difference between a setback and a blown account is the percentage risked per trade.
      </div>
    </div>
  );
}
